import { CreatorPermission, FeatureFlagName } from '@api'
import { Outlet } from 'react-router'
import { Navigate, type RouteObject } from 'react-router-dom'

import {
  AUTOMATED_MESSAGES_SUB_ITEMS,
  AUTOMATION_SUB_ITEMS,
  LISTS_SUB_ITEMS,
  PROFILE_SUB_ITEMS,
} from '@/components/layout/sidebar/constants.tsx'
import { FollowUpMessagesPage } from '@/modules/creator-settings/follow-up-messages/components/follow-up-messages-page'
import { FollowUpMessagesModule } from '@/modules/creator-settings/follow-up-messages/follow-up-messages-module'
import { ActionsRebillMessagesPage } from '@/modules/creator-settings/rebill-messages/components/create-rebill-messages-page.tsx'
import { EditRebillMessagePage } from '@/modules/creator-settings/rebill-messages/components/edit-rebill-message-page.tsx'
import { RebillMessagesPage } from '@/modules/creator-settings/rebill-messages/components/rebill-messages-page.tsx'
import { RebillMessagesModule } from '@/modules/creator-settings/rebill-messages/rebil-messages-module.tsx'
import { SettingsContextProvider } from '@/modules/creator-settings/state/SettingsProvider'
import { CreateWelcomeMessagesPage } from '@/modules/creator-settings/welcome-messages/components/create-welcome-messages-page.tsx'
import { EditWelcomeMessagePage } from '@/modules/creator-settings/welcome-messages/components/edit-welcome-message-page.tsx'
import { WelcomeMessagesPage } from '@/modules/creator-settings/welcome-messages/components/welcome-messages-page'
import { WelcomeMessagesModule } from '@/modules/creator-settings/welcome-messages/welcome-messages-module'
import { CreateExpiringFansMessagesPage } from '@/modules/expiring-fans/components/create-expiring-fans-messages-page.tsx'
import { EditExpiringFansMessagePage } from '@/modules/expiring-fans/components/edit-expiring-fans-message-page.tsx'
import { ExpiringFansPage } from '@/modules/expiring-fans/components/expiring-fans-page.tsx'
import { ExpiringFansModule } from '@/modules/expiring-fans/expiring-fans-module.tsx'
import { MessageHistoryModule } from '@/modules/log-of-all-messages/components/message-history-module.tsx'
import { MessageHistoryPage } from '@/modules/log-of-all-messages/components/message-history-page.tsx'
import { GeneralSettingsTab } from '@/modules/profile-settings/ui/GeneralSettingsTab.tsx'
import { NewSubscriberAnalysisPage } from '@/modules/tracking/new-subscriber-analysis/ui/new-subscriber-analysis-page.tsx'
import { AccountingPage } from '@/pages/accounting'
import { ActivateAccountPage } from '@/pages/activate-account.tsx'
import { ChattingPage } from '@/pages/chatting'
import { CreatorsPageView } from '@/pages/creators'
import { RestrictedWordsView } from '@/pages/restricted-words'
import { RestrictedWordsLogsView } from '@/pages/restricted-words/logs.tsx'
import { AutomationSettingsView } from '@/pages/settings/automation.tsx'
import { DisplaySettingsView } from '@/pages/settings/display.tsx'
import { CustomListsView } from '@/pages/settings/lists/custom-lists'
import { NoSpendersView } from '@/pages/settings/lists/no-spenders'
import { RecentSpendersView } from '@/pages/settings/lists/recent-spenders'
import { SharedFollowersView } from '@/pages/settings/lists/shared-followers'
import { VisibilitySiteView } from '@/pages/settings/visibility.tsx'
import { ShiftsView } from '@/pages/shifts'
import { StatisticsInstagramView } from '@/pages/statistics/instagram'
import { InstagramAccountsPage } from '@/pages/statistics/instagram/accounts.tsx'
import { TikTokVideos } from '@/pages/tiktok-research/TikTokVideos.tsx'
import { TikTokListDetails } from '@/pages/tiktok-research/lists/TikTokListDetails.tsx'
import { TikTokLists } from '@/pages/tiktok-research/lists/TikTokLists.tsx'
import { TikTokSounds } from '@/pages/tiktok-research/sounds/TikTokSounds.tsx'
import { TikTokSoundsTrendingVideos } from '@/pages/tiktok-research/sounds/TikTokSoundsTrendingVideos.tsx'
import { FunnelMonitoringView } from '@/pages/tracking/funnel'
import { PpvTrackingCategoryPage } from '@/pages/tracking/ppv/ppv-tracking-category-page.tsx'
import { PpvTrackingMessagesPage } from '@/pages/tracking/ppv/ppv-tracking-messages-page.tsx'
import { PpvTrackingOverviewPage } from '@/pages/tracking/ppv/ppv-tracking-overview-page.tsx'
import { VaultPage } from '@/pages/vault/VaultPage.tsx'
import {
  AiFeaturesRoutePart,
  AiFeaturesRoutesAICHAlphaSuffix,
  AiFeaturesRoutesSettingsSuffix,
  AppRoutes,
  AutomationSettingsRoutePart,
  CreatorSettingsScriptsSuffix,
  DisplaySettingsRoutePart,
  ExpiringFansRoutePart,
  FunnelMonitoringRoutePart,
  ListsRouteCustomListsSuffix,
  ListsRouteNewSubsListSuffix,
  ListsRouteNoSpendersSuffix,
  ListsRoutePart,
  ListsRouteRecentSpendersSuffix,
  ListsRouteSharedFollowersSuffix,
  ListsRouteSpendingListsSuffix,
  MessageHistoryRoutePart,
  OnlineMassMessagesRoutePart,
  PpvFollowUpMessagesRoutePart,
  RebillMessagesRoutePart,
  SettingsRouteAutomationsAutoFollowSuffix,
  SettingsRouteAutomationsFanNumberingSuffix,
  SettingsRouteAutomationsPromotionSuffix,
  SettingsRouteAutomationsSuffix,
  SettingsRoutePart,
  StatisticsRouteInstagramReelsSuffix,
  StatisticsRouteOnlyFansSuffix,
  StatisticsRoutePart,
  StatisticsRouteTiktokSuffix,
  TrackingRouteChatterSuffix,
  TrackingRouteFreeTrialLinksSuffix,
  TrackingRouteLinksSuffix,
  TrackingRouteNewSubscriberAnalysisSuffix,
  TrackingRoutePart,
  TrackingRoutePpvSuffix,
  VaultRoutePart,
  VisibilitySettingsRoutePart,
  WelcomeMessagesRoutePart,
} from '@/utils/constants'
import { PaginationProvider } from '@/utils/pagination/pagination-context.tsx'

import { App } from './App'
import { Layout } from './components/layout/layout.tsx'
import { SubSidebar } from './components/layout/sub-sidebar.tsx'
import { AccessControlGuard } from './modules/accounts/state/AccessControlGuard'
import { CreatorFeatures } from './modules/accounts/types'
import { CourseProvider } from './modules/course/state/CourseProvider'
import { ChatterLeaderboardInspectView } from './modules/overview/ui/leaderboard/chatter-leaderboard-inspect-view.tsx'
import { ProfileSettingsTab } from './modules/profile-settings/ui/ProfileSettingsTab'
import { SecuritySettingsTab } from './modules/profile-settings/ui/SecuritySettingsTab'
import { AboutCreatorHero } from './modules/profile-settings/ui/about-creator-hero.tsx'
import { PpvTrackingProvider } from './modules/tracking/ppv/state/PpvTrackingProvider.tsx'
import { OverviewView } from './pages'
import { AiSettingsPage } from './pages/ai-features/ai-settings.tsx'
import { AICHAlphaPage } from './pages/ai-features/aich-alpha.tsx'
import { ApplyPromoPage } from './pages/apply-promo.tsx'
import { CourseView } from './pages/course/course'
import { CoursesView } from './pages/course/courses'
import { CreatorsAccessView } from './pages/creators/access.tsx'
import { LicensesPage } from './pages/licenses'
import { YourLicensesPage } from './pages/licenses/your-licenses-page'
import { LoginView } from './pages/login'
import { OnlineMassMessagesView } from './pages/online-mass-messages'
import { CreateMassMessageView } from './pages/online-mass-messages/create'
import { MassMessageEdit } from './pages/online-mass-messages/edit'
import { ProspectsView } from './pages/prospects/prospects.tsx'
import { Questionnaire } from './pages/questionnaire.tsx'
import { RegisterView } from './pages/register'
import { RegisterAfterPaymentPage } from './pages/register-after-payment'
import { ResetPasswordPage } from './pages/reset-password'
import { AutoFollowView } from './pages/settings/automations/auto-follow'
import { FanNumberingView } from './pages/settings/automations/fan-numbering'
import { PromotionReactivatorView } from './pages/settings/automations/promotion-reactivator'
import { ScriptPage } from './pages/settings/scripts'
import { CreateScriptPage } from './pages/settings/scripts/create'
import { EditScriptPage } from './pages/settings/scripts/edit'
import { TierView } from './pages/settings/tiers'
import { NewSubsListView } from './pages/settings/tiers/lists'
import { ShiftsHistoryView } from './pages/shifts/history.tsx'
import { StatisticsOnlyFansPage } from './pages/statistics/onlyfans'
import { StatisticsTikTokView } from './pages/statistics/tiktok'
import { TikTokAccountsPage } from './pages/statistics/tiktok/accounts'
import { TasksView } from './pages/tasks'
import { TaskListView } from './pages/tasks/list'
import { TeamView } from './pages/team'
import { TeamEditView } from './pages/team/edit'
import { TeamRoleEditView } from './pages/team/roles/role-edit.tsx'
import { TeamRolesView } from './pages/team/roles/roles.tsx'
import { ChatterTrackingView } from './pages/tracking/chatter-tracking'
import { ChatterInspectView } from './pages/tracking/chatter-tracking/inspect'
import { FreeTrialLinksPage } from './pages/tracking/links/free-trial-links-page.tsx'
import { TrackingLinksInspectPage } from './pages/tracking/links/tracking-links-inspect-page.tsx'
import { TrackingLinksPage } from './pages/tracking/links/tracking-links-page.tsx'
import { VipSupportView } from './pages/vip-support.tsx'

/**
 *
 */
export const appRoutes: RouteObject[] = [
  {
    element: <App />,
    children: [
      // Authentication
      {
        path: AppRoutes.Auth.Login.asRouteDescriptor(),
        element: <LoginView />,
      },
      {
        path: AppRoutes.Auth.Register.asRouteDescriptor(),
        element: <RegisterView />,
      },
      {
        path: AppRoutes.Auth.RegisterAfterPayment.asRouteDescriptor(),
        element: <RegisterAfterPaymentPage />,
      },
      {
        path: AppRoutes.Auth.ResetPassword.asRouteDescriptor(),
        element: <ResetPasswordPage />,
      },
      {
        path: AppRoutes.Auth.ResetPasswordWithToken.asRouteDescriptor(),
        element: <ResetPasswordPage />,
      },
      {
        path: AppRoutes.Auth.ActivateAccount.asRouteDescriptor(),
        element: <ActivateAccountPage />,
      },
      {
        path: AppRoutes.Auth.ApplyPromo.asRouteDescriptor(),
        element: <ApplyPromoPage />,
      },

      // Sidebar
      {
        element: <Layout />,
        children: [
          {
            index: true,
            path: '*',
            element: (
              <Navigate
                to={AppRoutes.Home.asUrl()}
                replace
              />
            ),
          },

          {
            path: '/',
            element: <OverviewView />,
          },
          {
            path: AppRoutes.ChatterLeaderboardBrowser.asRouteDescriptor(),
            element: <ChatterLeaderboardInspectView />,
          },
          {
            path: AppRoutes.ChatterLeaderboardUser.asRouteDescriptor(),
            element: <ChatterLeaderboardInspectView />,
          },

          // Task
          {
            path: AppRoutes.Tasks.List.asRouteDescriptor(),
            element: (
              <AccessControlGuard
                featureDisplayName="Tasks"
                accountFeature={CreatorFeatures.tasks}
              >
                <TasksView />
              </AccessControlGuard>
            ),
          },

          {
            path: AppRoutes.Tasks.ListDetails.asRouteDescriptor(),
            element: <TaskListView />,
          },

          // Team
          {
            path: AppRoutes.Teams.Base.asRouteDescriptor(),
            children: [
              {
                path: AppRoutes.Teams.Members.List.asRouteDescriptor(),
                element: (
                  <AccessControlGuard
                    featureDisplayName="Team"
                    onlyAdmin
                  >
                    <TeamView />
                  </AccessControlGuard>
                ),
              },
              {
                path: AppRoutes.Teams.Members.Details.asRouteDescriptor(),
                element: <TeamEditView />,
              },
              {
                path: AppRoutes.Teams.Roles.List.asRouteDescriptor(),
                element: (
                  <AccessControlGuard
                    featureDisplayName="Team"
                    onlyAdmin
                  >
                    <TeamRolesView />
                  </AccessControlGuard>
                ),
              },
              {
                path: AppRoutes.Teams.Roles.Edit.asRouteDescriptor(),
                element: (
                  <AccessControlGuard
                    featureDisplayName="Team"
                    onlyAdmin
                  >
                    <TeamRoleEditView />
                  </AccessControlGuard>
                ),
              },
            ],
          },

          // Creators
          {
            path: AppRoutes.Shifts.List.asRouteDescriptor(),
            element: (
              <AccessControlGuard
                featureDisplayName="Shifts"
                creatorRequired
              >
                <ShiftsView />
              </AccessControlGuard>
            ),
          },
          {
            path: AppRoutes.Shifts.History.asRouteDescriptor(),
            element: <ShiftsHistoryView />,
          },

          {
            path: AppRoutes.Creators.List.asRouteDescriptor(),
            element: <CreatorsPageView />,
          },
          {
            path: AppRoutes.Creators.Access.asRouteDescriptor(),
            element: <CreatorsAccessView />,
          },
          {
            path: AppRoutes.RestrictedWords.List.asRouteDescriptor(),
            element: (
              <AccessControlGuard
                featureDisplayName="Restricted Words"
                creatorRequired
                onlyAdmin
              >
                <RestrictedWordsView />
              </AccessControlGuard>
            ),
          },
          {
            path: AppRoutes.Prospects.List.asRouteDescriptor(),
            element: (
              <AccessControlGuard
                featureDisplayName="Prospects"
                accountFeature={CreatorFeatures.prospecting}
                onlyAdmin
              >
                <PaginationProvider>
                  <ProspectsView />
                </PaginationProvider>
              </AccessControlGuard>
            ),
          },
          {
            path: AppRoutes.RestrictedWords.Logs.asRouteDescriptor(),
            element: (
              <PaginationProvider>
                <RestrictedWordsLogsView />
              </PaginationProvider>
            ),
          },

          // Everything creator specific
          {
            path: '/c/:creatorId',
            children: [
              // Statistics
              {
                path: StatisticsRoutePart,
                children: [
                  {
                    path: StatisticsRouteOnlyFansSuffix,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Statistics"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.stats}
                      >
                        <StatisticsOnlyFansPage />
                      </AccessControlGuard>
                    ),
                  },
                  {
                    path: StatisticsRouteTiktokSuffix,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="TikTok tracking"
                        creatorPermission={
                          CreatorPermission.MODIFY_SOCIAL_ACCOUNTS
                        }
                        creatorFeature={CreatorFeatures.tiktok}
                      >
                        <StatisticsTikTokView />
                      </AccessControlGuard>
                    ),
                  },

                  {
                    path: AppRoutes.Creator.Statistics.TikTokAccounts.asRouteDescriptor(),
                    element: (
                      <AccessControlGuard
                        featureDisplayName="TikTok tracking"
                        creatorPermission={
                          CreatorPermission.MODIFY_SOCIAL_ACCOUNTS
                        }
                        creatorFeature={CreatorFeatures.tiktok}
                      >
                        <TikTokAccountsPage />
                      </AccessControlGuard>
                    ),
                  },
                  {
                    path: StatisticsRouteInstagramReelsSuffix,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Instagram Reels tracking"
                        creatorPermission={
                          CreatorPermission.MODIFY_SOCIAL_ACCOUNTS
                        }
                        creatorFeature={CreatorFeatures.instagram}
                      >
                        <StatisticsInstagramView />
                      </AccessControlGuard>
                    ),
                  },

                  {
                    path: AppRoutes.Creator.Statistics.InstagramReelsAccounts.asRouteDescriptor(),
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Instagram Reels tracking"
                        creatorPermission={
                          CreatorPermission.MODIFY_SOCIAL_ACCOUNTS
                        }
                        creatorFeature={CreatorFeatures.instagram}
                      >
                        <InstagramAccountsPage />
                      </AccessControlGuard>
                    ),
                  },
                ],
              },

              // Tracking

              {
                path: TrackingRoutePart,
                children: [
                  {
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Chatter Tracking"
                        creatorPermission={
                          CreatorPermission.SEE_CHATTER_TRACKING
                        }
                        creatorFeature={CreatorFeatures.tracking}
                      />
                    ),
                    children: [
                      {
                        path: TrackingRouteChatterSuffix,
                        element: <ChatterTrackingView />,
                      },
                      {
                        path: `${TrackingRouteChatterSuffix}/browser/:name`,
                        element: <ChatterInspectView />,
                      },
                      {
                        path: `${TrackingRouteChatterSuffix}/user/:userId`,
                        element: <ChatterInspectView />,
                      },
                    ],
                  },
                  // PPV Tracking
                  {
                    element: (
                      <AccessControlGuard
                        featureDisplayName="PPV Tracking"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.script_tracking}
                      >
                        <PpvTrackingProvider />
                      </AccessControlGuard>
                    ),
                    children: [
                      {
                        path: TrackingRoutePpvSuffix,
                        element: <PpvTrackingOverviewPage />,
                      },
                      {
                        path: `${TrackingRoutePpvSuffix}/:categoryIndex`,
                        element: <PpvTrackingCategoryPage />,
                      },
                      {
                        path: `${TrackingRoutePpvSuffix}/:categoryIndex/messages/:mediaSetIndex`,
                        element: <PpvTrackingMessagesPage />,
                      },
                    ],
                  },
                  // New Subscriber Analysis
                  {
                    element: (
                      <AccessControlGuard
                        featureDisplayName="New Subscriber Analysis"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.tracking}
                      >
                        <NewSubscriberAnalysisPage />
                      </AccessControlGuard>
                    ),
                    path: TrackingRouteNewSubscriberAnalysisSuffix,
                  },
                  // Links Tracking
                  {
                    path: TrackingRouteLinksSuffix,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Tracking Links"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.tracking}
                      >
                        <TrackingLinksPage />
                      </AccessControlGuard>
                    ),
                  },
                  {
                    path: TrackingRouteLinksSuffix + '/:linkId',
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Tracking Links"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.tracking}
                      >
                        <TrackingLinksInspectPage type="Tracking" />
                      </AccessControlGuard>
                    ),
                  },
                  {
                    path: TrackingRouteFreeTrialLinksSuffix,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Free Trial Links"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.tracking}
                      >
                        <FreeTrialLinksPage />
                      </AccessControlGuard>
                    ),
                  },
                  {
                    path: TrackingRouteFreeTrialLinksSuffix + '/:linkId',
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Free Trial Links"
                        creatorPermission={CreatorPermission.SEE_STATS}
                        creatorFeature={CreatorFeatures.tracking}
                      >
                        <TrackingLinksInspectPage type="Trial" />
                      </AccessControlGuard>
                    ),
                  },
                  // Message History
                  {
                    path: MessageHistoryRoutePart,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Message History"
                        creatorPermission={
                          CreatorPermission.SEE_CHATTER_TRACKING
                        }
                        creatorFeature={CreatorFeatures.tracking}
                      >
                        <MessageHistoryModule>
                          <MessageHistoryPage />
                        </MessageHistoryModule>
                      </AccessControlGuard>
                    ),
                  },
                  // Funnel Monitoring
                  {
                    path: FunnelMonitoringRoutePart,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Funnel Monitoring"
                        creatorFeature={CreatorFeatures.tracking}
                        creatorPermission={
                          CreatorPermission.MODIFY_SOCIAL_ACCOUNTS
                        }
                      >
                        <FunnelMonitoringView />
                      </AccessControlGuard>
                    ),
                  },
                ],
              },

              // AI Features
              {
                path: AiFeaturesRoutePart,
                children: [
                  // AI Translator
                  {
                    path: AiFeaturesRoutesSettingsSuffix,
                    element: (
                      <AccessControlGuard
                        creatorFeature={CreatorFeatures.display_settings}
                        featureDisplayName="AI Translator"
                      >
                        <SettingsContextProvider key="ai-settings">
                          <AiSettingsPage />
                        </SettingsContextProvider>
                      </AccessControlGuard>
                    ),
                  },
                  {
                    path: AiFeaturesRoutesAICHAlphaSuffix,
                    element: (
                      <AccessControlGuard
                        creatorFeature={CreatorFeatures.display_settings}
                        featureDisplayName="AICH Alpha"
                        featureFlagName={FeatureFlagName.AI_CHATTING_FEATURE}
                        onlyAdmin
                      >
                        <SettingsContextProvider key="aich-alpha">
                          <AICHAlphaPage />
                        </SettingsContextProvider>
                      </AccessControlGuard>
                    ),
                  },
                ],
              },

              // Creators Settings
              {
                path: SettingsRoutePart,
                children: [
                  // Automations
                  {
                    path: SettingsRouteAutomationsSuffix,
                    element: (
                      <SettingsContextProvider key="automations">
                        <SubSidebar routes={AUTOMATION_SUB_ITEMS} />
                      </SettingsContextProvider>
                    ),
                    children: [
                      {
                        path: SettingsRouteAutomationsPromotionSuffix,
                        element: (
                          <AccessControlGuard
                            creatorFeature={
                              CreatorFeatures.promotion_automations
                            }
                            creatorPermission={
                              CreatorPermission.MODIFY_SETTINGS
                            }
                            featureDisplayName="Promotion Reactivator"
                          >
                            <PromotionReactivatorView />
                          </AccessControlGuard>
                        ),
                      },
                      {
                        path: SettingsRouteAutomationsAutoFollowSuffix,
                        element: (
                          <AccessControlGuard
                            featureDisplayName="Auto Follow"
                            creatorPermission={
                              CreatorPermission.MODIFY_SETTINGS
                            }
                            creatorFeature={CreatorFeatures.auto_follow}
                          >
                            <AutoFollowView />
                          </AccessControlGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: `${SettingsRouteAutomationsSuffix}/${SettingsRouteAutomationsFanNumberingSuffix}`,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Fan Numbering"
                        creatorRequired
                        creatorPermission={
                          CreatorPermission.MODIFY_FAN_NUMBERING
                        }
                        creatorFeature={CreatorFeatures.fan_numbering}
                      >
                        <SettingsContextProvider key="automations">
                          <FanNumberingView />
                        </SettingsContextProvider>
                      </AccessControlGuard>
                    ),
                  },
                  {
                    element: (
                      <SettingsContextProvider key="messages">
                        <SubSidebar routes={AUTOMATED_MESSAGES_SUB_ITEMS} />
                      </SettingsContextProvider>
                    ),
                    children: [
                      {
                        path: WelcomeMessagesRoutePart,
                        element: (
                          <AccessControlGuard
                            featureDisplayName="Automated Messages"
                            creatorPermission={
                              CreatorPermission.MODIFY_SETTINGS
                            }
                            creatorFeature={CreatorFeatures.welcome_messages}
                          >
                            <WelcomeMessagesModule>
                              <Outlet />
                            </WelcomeMessagesModule>
                          </AccessControlGuard>
                        ),
                        children: [
                          {
                            path: '',
                            element: <WelcomeMessagesPage />,
                          },
                          {
                            path: 'create',
                            element: <CreateWelcomeMessagesPage />,
                          },
                          {
                            path: 'edit/:id',
                            element: <EditWelcomeMessagePage />,
                          },
                        ],
                      },
                      {
                        path: PpvFollowUpMessagesRoutePart,
                        element: (
                          <AccessControlGuard
                            featureDisplayName="PPV Follow Up Messages"
                            creatorPermission={
                              CreatorPermission.MODIFY_SETTINGS
                            }
                            creatorFeature={CreatorFeatures.follow_up_messages}
                          >
                            <FollowUpMessagesModule>
                              <FollowUpMessagesPage />
                            </FollowUpMessagesModule>
                          </AccessControlGuard>
                        ),
                      },
                      {
                        path: RebillMessagesRoutePart,
                        element: (
                          <AccessControlGuard
                            featureDisplayName="Rebill Messages"
                            creatorFeature={CreatorFeatures.rebill_messages}
                            creatorPermission={
                              CreatorPermission.MODIFY_SETTINGS
                            }
                          >
                            <RebillMessagesModule>
                              <Outlet />
                            </RebillMessagesModule>
                          </AccessControlGuard>
                        ),
                        children: [
                          {
                            path: '',
                            element: <RebillMessagesPage />,
                          },
                          {
                            path: 'create',
                            element: <ActionsRebillMessagesPage />,
                          },
                          {
                            path: 'edit/:id',
                            element: <EditRebillMessagePage />,
                          },
                        ],
                      },
                    ],
                  },
                  // Scripts
                  {
                    path: CreatorSettingsScriptsSuffix,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Scripts"
                        creatorPermission={CreatorPermission.MODIFY_SETTINGS}
                        creatorFeature={CreatorFeatures.scripts}
                      >
                        <Outlet />
                      </AccessControlGuard>
                    ),
                    children: [
                      {
                        path: '',
                        element: <ScriptPage />,
                      },

                      {
                        path: `create`,
                        element: <CreateScriptPage />,
                      },
                      {
                        path: `:id/edit`,
                        element: <EditScriptPage />,
                      },
                    ],
                  },

                  // Bulk Messages
                  {
                    path: OnlineMassMessagesRoutePart,
                    element: (
                      <SettingsContextProvider key="online-mass-messages">
                        <AccessControlGuard
                          featureDisplayName="Online Mass Messages"
                          creatorPermission={
                            CreatorPermission.MODIFY_ONLINE_MASS_MESSAGES
                          }
                          creatorFeature={CreatorFeatures.bulk_messages}
                        />
                      </SettingsContextProvider>
                    ),
                    children: [
                      {
                        path: '',
                        element: <OnlineMassMessagesView />,
                      },
                      {
                        path: `create`,
                        element: <CreateMassMessageView />,
                      },
                      {
                        path: `edit/:id`,
                        element: <MassMessageEdit />,
                      },
                    ],
                  },

                  // Vault
                  {
                    path: VaultRoutePart,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Vault"
                        creatorPermission={
                          CreatorPermission.SEE_VAULT_IN_DASHBOARD
                        }
                        creatorFeature={CreatorFeatures.better_vault}
                      >
                        <VaultPage />
                      </AccessControlGuard>
                    ),
                  },

                  // Lists
                  {
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Lists"
                        creatorPermission={CreatorPermission.MODIFY_SETTINGS}
                        creatorFeature={CreatorFeatures.lists}
                      >
                        <SubSidebar routes={LISTS_SUB_ITEMS} />
                      </AccessControlGuard>
                    ),
                    path: ListsRoutePart,
                    children: [
                      {
                        path: ListsRouteNewSubsListSuffix,
                        element: <NewSubsListView />,
                      },
                      {
                        path: ListsRouteSpendingListsSuffix,
                        element: (
                          <AccessControlGuard
                            featureDisplayName="Fan Spend Lists"
                            creatorPermission={
                              CreatorPermission.MODIFY_SETTINGS
                            }
                            creatorFeature={CreatorFeatures.tiers}
                          >
                            <TierView />
                          </AccessControlGuard>
                        ),
                      },
                      {
                        path: ListsRouteNoSpendersSuffix,
                        element: <NoSpendersView />,
                      },
                      {
                        path: ListsRouteRecentSpendersSuffix,
                        element: <RecentSpendersView />,
                      },
                      {
                        path: ListsRouteSharedFollowersSuffix,
                        element: <SharedFollowersView />,
                      },
                      {
                        path: ListsRouteCustomListsSuffix,
                        element: <CustomListsView />,
                      },
                    ],
                  },

                  // Expiring Fans
                  {
                    path: ExpiringFansRoutePart,
                    element: (
                      <SettingsContextProvider key="expiring_fans">
                        <AccessControlGuard
                          featureDisplayName="Expiring Fans"
                          creatorPermission={CreatorPermission.MODIFY_SETTINGS}
                          creatorFeature={CreatorFeatures.expiring_fans}
                        >
                          <ExpiringFansModule>
                            <Outlet />
                          </ExpiringFansModule>
                        </AccessControlGuard>
                      </SettingsContextProvider>
                    ),
                    children: [
                      {
                        path: '',
                        element: <ExpiringFansPage />,
                      },
                      {
                        path: `create`,
                        element: <CreateExpiringFansMessagesPage />,
                      },
                      {
                        path: `edit/:id`,
                        element: <EditExpiringFansMessagePage />,
                      },
                    ],
                  },

                  // Display Settings
                  {
                    path: DisplaySettingsRoutePart,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Display Settings"
                        creatorPermission={CreatorPermission.MODIFY_SETTINGS}
                        creatorFeature={CreatorFeatures.display_settings}
                      >
                        <SettingsContextProvider key="display_settings">
                          <DisplaySettingsView />
                        </SettingsContextProvider>
                      </AccessControlGuard>
                    ),
                  },

                  // Automation Settings
                  {
                    path: AutomationSettingsRoutePart,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Automation Settings"
                        creatorPermission={CreatorPermission.MODIFY_SETTINGS}
                        creatorFeature={CreatorFeatures.automation_settings}
                      >
                        <SettingsContextProvider key="automation_settings">
                          <AutomationSettingsView />
                        </SettingsContextProvider>
                      </AccessControlGuard>
                    ),
                  },

                  // Visibility
                  {
                    path: VisibilitySettingsRoutePart,
                    element: (
                      <AccessControlGuard
                        featureDisplayName="Visibility"
                        creatorPermission={CreatorPermission.MODIFY_SETTINGS}
                        creatorFeature={CreatorFeatures.visibility}
                      >
                        <SettingsContextProvider key="visibility_settings">
                          <VisibilitySiteView />
                        </SettingsContextProvider>
                      </AccessControlGuard>
                    ),
                  },
                ],
              },
            ],
          },

          // Courses
          {
            element: (
              <AccessControlGuard
                featureDisplayName="Course"
                onlyAdmin
              >
                <CourseProvider />
              </AccessControlGuard>
            ),
            children: [
              {
                path: AppRoutes.Courses.Details.asRouteDescriptor(),
                element: <CourseView />,
              },
              {
                path: AppRoutes.Courses.List.asRouteDescriptor(),
                element: <CoursesView />,
              },
            ],
          },
          //Accounting
          {
            path: AppRoutes.Accounting.Index.asRouteDescriptor(),
            element: (
              <AccessControlGuard
                featureDisplayName="Accounting site"
                accountFeature={CreatorFeatures.accounting_site}
                onlyAdmin
              >
                <AccountingPage />
              </AccessControlGuard>
            ),
          },
          // Tiktok Research
          {
            element: (
              <AccessControlGuard
                featureDisplayName="TikTok Research"
                accountFeature={CreatorFeatures.tiktok_research}
                featureFlagName={FeatureFlagName.TIK_TOK_RESEARCH_FEATURE}
                onlyAdmin
              >
                <Outlet />
              </AccessControlGuard>
            ),
            children: [
              {
                path: AppRoutes.TiktokResearch.Videos.asRouteDescriptor(),
                element: <TikTokVideos />,
              },
              {
                path: AppRoutes.TiktokResearch.Sounds.Index.asRouteDescriptor(),
                children: [
                  {
                    path: '',
                    element: <TikTokSounds />,
                  },
                  {
                    path: ':soundId/trending-videos',
                    element: <TikTokSoundsTrendingVideos />,
                  },
                ],
              },
              {
                path: AppRoutes.TiktokResearch.Lists.Index.asRouteDescriptor(),
                children: [
                  {
                    path: '',
                    element: <TikTokLists />,
                  },
                  {
                    path: ':id',
                    element: <TikTokListDetails />,
                  },
                  {
                    path: ':listId/trending-videos/:soundId',
                    element: <TikTokSoundsTrendingVideos />,
                  },
                ],
              },
            ],
          },
          // Licenses
          {
            path: AppRoutes.Licenses.Index.asRouteDescriptor(),
            element: (
              <AccessControlGuard
                featureDisplayName="Licenses"
                onlyOwner
              >
                <LicensesPage />
              </AccessControlGuard>
            ),
          },
          {
            path: AppRoutes.Licenses.YourLicenses.asRouteDescriptor(),
            element: <YourLicensesPage />,
          },
          // VIP Support
          {
            path: AppRoutes.Help.VipSupport.asRouteDescriptor(),
            element: <VipSupportView />,
          },

          {
            path: '/profile/',
            element: <SubSidebar routes={PROFILE_SUB_ITEMS} />,
            children: [
              {
                path: AppRoutes.Profile.Settings.asRouteDescriptor(),
                element: <ProfileSettingsTab />,
              },
              {
                path: AppRoutes.Profile.GeneralSettings.asRouteDescriptor(),
                element: <GeneralSettingsTab />,
              },
              {
                path: AppRoutes.Profile.Security.asRouteDescriptor(),
                element: <SecuritySettingsTab />,
              },
              {
                path: AppRoutes.Profile.AboutUs.asRouteDescriptor(),
                element: <AboutCreatorHero />,
              },
            ],
          },
        ],
      },

      {
        path: AppRoutes.Questionnaire.asRouteDescriptor(),
        element: (
          <AccessControlGuard
            featureDisplayName=""
            featureFlagName={FeatureFlagName.ONBOARDING}
            onlyOwner
          >
            <Questionnaire />
          </AccessControlGuard>
        ),
      },

      // Chatting ui
      {
        path: AppRoutes.Chatting.ChatWindow.asRouteDescriptor(),
        element: (
          <SettingsContextProvider key="chatting">
            <ChattingPage />
          </SettingsContextProvider>
        ),
      },
    ],
  },
]
